import { useMemo, useState, type FC } from "react";
import { useTranslation } from "react-i18next";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { DefinitionTooltip } from "src/components/common";
import { formatNumber } from "src/utils/numberFormat";
import type { PaidMediaOverviewGranularity } from "src/services/PaidMediaOverviewAPI";
import { normalizeTooltipValue, OrganicChartTooltip } from "../metaAds/metaChartConfig";
import type { MockDailyFact } from "../../data/paidMediaOverviewMockData";
import { bucketPlatformReportSeries } from "../../data/paidMediaPlatformReportMockData";
import { formatOverviewBucketLabel } from "../overview/overviewChartConfig";
import { usePaidMediaChartRtl } from "../../utils/paidMediaChartRtl";
import {
  getPlatformTrendChartMargin,
  PLATFORM_TREND_Y_AXIS,
  renderPlatformTrendAxisTick,
  renderPlatformTrendSecondaryAxisTick,
} from "./platformReportChartParts";
import {
  platformReportSectionHelp,
  PLATFORM_REPORT_TREND_METRICS,
  type PlatformReportTrendMetric,
} from "./platformReportConfig";
import { formatPlatformReportValue } from "./platformReportFormat";

const TREND_PAGE_SIZE = 15;

interface PlatformReportTrendsPanelProps {
  series: MockDailyFact[];
  granularity: PaidMediaOverviewGranularity;
  onGranularityChange: (_granularity: PaidMediaOverviewGranularity) => void;
  clickLabel: string;
}

const PlatformReportTrendsPanel: FC<PlatformReportTrendsPanelProps> = ({
  series,
  granularity,
  onGranularityChange,
  clickLabel,
}) => {
  const { t, i18n } = useTranslation();
  const chartRtl = usePaidMediaChartRtl();
  const [activeMetrics, setActiveMetrics] = useState<PlatformReportTrendMetric[]>([
    "spend",
    "impressions",
    "clicks",
    "purchases",
  ]);
  const [page, setPage] = useState(1);

  const PreviousIcon = chartRtl.isRTL ? ChevronRight : ChevronLeft;
  const NextIcon = chartRtl.isRTL ? ChevronLeft : ChevronRight;

  const data = useMemo(
    () =>
      bucketPlatformReportSeries(series, granularity).map(fact => ({
        date: formatOverviewBucketLabel(
          fact.date,
          granularity,
          date => t("paidMediaOverview.trend.weekOf", { date }),
          i18n.language
        ),
        spend: fact.spend,
        impressions: fact.impressions,
        clicks: fact.clicks,
        purchases: fact.purchases,
      })),
    [series, granularity, t, i18n.language]
  );

  const totalPages = Math.max(1, Math.ceil(data.length / TREND_PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pagedData = data.slice(
    (currentPage - 1) * TREND_PAGE_SIZE,
    currentPage * TREND_PAGE_SIZE
  );

  const paginationLabel = t("pagination.pageOf", {
    current: formatNumber(currentPage, {}, i18n.language),
    total: formatNumber(totalPages, {}, i18n.language),
  });

  const metricLabel = (metric: PlatformReportTrendMetric): string =>
    metric === "clicks" ? clickLabel : t(`platformAdsReport.kpis.${metric}`);

  const toggleMetric = (metric: PlatformReportTrendMetric) =>
    setActiveMetrics(current =>
      current.includes(metric)
        ? current.filter(entry => entry !== metric)
        : [...current, metric]
    );

  const title = t("platformAdsReport.sections.performanceTrends");

  return (
    <article className="paid-media-panel paid-media-panel--trend">
      <div className="paid-media-panel__header paid-media-panel__header--trend">
        <div className="paid-media-panel__heading">
          <h3>{title}</h3>
          <DefinitionTooltip
            label={title}
            info={platformReportSectionHelp(t, "performanceTrends")}
          />
        </div>
        <div className="paid-media-panel__header-actions paid-media-panel__header-actions--trend">
          <fieldset className="paid-media-trend-granularity">
            <legend className="paid-media-visually-hidden">
              {t("metaAdsReport.trends.granularityAria")}
            </legend>
            {(["daily", "monthly"] as const).map(option => (
              <button
                key={option}
                type="button"
                className={
                  granularity === option
                    ? "paid-media-trend-granularity__btn paid-media-trend-granularity__btn--active"
                    : "paid-media-trend-granularity__btn"
                }
                onClick={() => {
                  onGranularityChange(option);
                  setPage(1);
                }}
              >
                {t(`metaAdsReport.trends.${option}`)}
              </button>
            ))}
          </fieldset>
          <fieldset className="paid-media-trend-chips paid-media-trend-chips--legend">
            <legend className="paid-media-visually-hidden">
              {t("metaAdsReport.trends.metricsAria")}
            </legend>
            {PLATFORM_REPORT_TREND_METRICS.map(metric => (
              <button
                key={metric.key}
                type="button"
                className={
                  activeMetrics.includes(metric.key)
                    ? "paid-media-trend-chips__item paid-media-trend-chips__item--active"
                    : "paid-media-trend-chips__item"
                }
                onClick={() => toggleMetric(metric.key)}
              >
                <span
                  className={`paid-media-trend-chips__dot platform-report-trend-dot--${metric.key}`}
                  aria-hidden
                />
                {metricLabel(metric.key)}
              </button>
            ))}
          </fieldset>
        </div>
      </div>
      <div className="paid-media-chart paid-media-chart--trend platform-report__trends-chart">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={pagedData}
            margin={getPlatformTrendChartMargin(chartRtl.isRTL)}
          >
            <CartesianGrid stroke="#dce3ef" strokeDasharray="3 3" />
            <XAxis
              dataKey="date"
              reversed={chartRtl.axisReversed}
              tick={{ fontSize: 11, fill: "#60718b" }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              yAxisId="primary"
              width={PLATFORM_TREND_Y_AXIS.primaryWidth}
              tickMargin={PLATFORM_TREND_Y_AXIS.tickMargin}
              orientation={chartRtl.yAxisOrientation}
              tick={renderPlatformTrendAxisTick(i18n.language, chartRtl.yAxisOrientation)}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              yAxisId="secondary"
              orientation={chartRtl.oppositeYAxisOrientation}
              width={PLATFORM_TREND_Y_AXIS.secondaryWidth}
              tickMargin={PLATFORM_TREND_Y_AXIS.tickMargin}
              tick={renderPlatformTrendSecondaryAxisTick(
                i18n.language,
                chartRtl.oppositeYAxisOrientation
              )}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip
              content={({ active, label, payload }) => (
                <OrganicChartTooltip
                  active={active}
                  label={String(label ?? "")}
                  labelTitle={t("metaAdsReport.chart.date")}
                  payload={payload}
                  seriesLabelForDataKey={key => metricLabel(key as PlatformReportTrendMetric)}
                  valueFormatter={(value, name) => {
                    const metric = PLATFORM_REPORT_TREND_METRICS.find(
                      entry => entry.key === name
                    );
                    return metric
                      ? formatPlatformReportValue(Number(value ?? 0), metric.format)
                      : String(normalizeTooltipValue(value));
                  }}
                />
              )}
            />
            {PLATFORM_REPORT_TREND_METRICS.filter(metric =>
              activeMetrics.includes(metric.key)
            ).map(metric => (
              <Line
                key={metric.key}
                type="monotone"
                dataKey={metric.key}
                name={metric.key}
                yAxisId={metric.axis}
                stroke={metric.color}
                strokeWidth={2.4}
                dot={false}
                activeDot={{ r: 4 }}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
      <nav className="paid-media-trend-pagination" aria-label={paginationLabel}>
        <span className="paid-media-trend-pagination__label">{paginationLabel}</span>
        <div className="paid-media-trend-pagination__nav">
          <button
            type="button"
            className="paid-media-trend-pagination__btn"
            disabled={currentPage <= 1}
            onClick={() => setPage(current => Math.max(1, current - 1))}
            aria-label={t("pagination.previousPage")}
          >
            <PreviousIcon size={16} aria-hidden />
          </button>
          <button
            type="button"
            className="paid-media-trend-pagination__btn"
            disabled={currentPage >= totalPages}
            onClick={() => setPage(current => Math.min(totalPages, current + 1))}
            aria-label={t("pagination.nextPage")}
          >
            <NextIcon size={16} aria-hidden />
          </button>
        </div>
      </nav>
    </article>
  );
};

export default PlatformReportTrendsPanel;
